import React, { useEffect } from 'react';
import ReactModal from 'react-modal';
import classNames from 'classnames';
import styles from './modal.css';

interface ModalProps {
  children?: React.ReactNode;
  /** Boolean describing if the modal should be shown or not. Defaults to false. */
  isOpen: boolean;

  /** Object indicating styles to be used for the modal, divided into overlay and content styles. */
  style?: ReactModal.Styles;

  /** String className to be applied to the portal. Defaults to "ReactModalPortal". */
  portalClassName?: string;

  /** String className to be applied to the document.body. */
  bodyOpenClassName?: string;

  /** String className to be applied to the document.html. */
  htmlOpenClassName?: string;

  /** String or object className to be applied to the modal content. */
  className?: string | ReactModal.Classes;

  /** String or object className to be applied to the overlay. */
  overlayClassName?: string | ReactModal.Classes;

  /** Set this to properly hide your application from assistive screenreaders and other assistive technologies while the modal is open. */
  appElement?: HTMLElement | {};

  /** Number indicating the milliseconds to wait before closing the modal. Defaults to zero (no timeout). */
  closeTimeoutMS?: number;

  /** Boolean indicating if the appElement should be hidden. Defaults to true. */
  ariaHideApp?: boolean;

  /** Boolean indicating if the modal should be focused after render */
  shouldFocusAfterRender?: boolean;

  /** Boolean indicating if the overlay should close the modal. Defaults to true. */
  shouldCloseOnOverlayClick?: boolean;

  /** Boolean indicating if pressing the esc key should close the modal */
  shouldCloseOnEsc?: boolean;

  /** Boolean indicating if the modal should restore focus to the element that had focus prior to its display. */
  shouldReturnFocusAfterClose?: boolean;

  /** Additional aria attributes. */
  aria?: ReactModal.Aria;

  /** Additional data attributes to be applied to to the modal content in the form of "data-*" */
  data?: any;

  /** String indicating the role of the modal, allowing the 'dialog' role to be applied if desired. */
  role?: string;

  /** String indicating how the content container should be announced to screenreaders. */
  contentLabel?: string;

  /** Function accepting the ref for the content */
  contentRef?: (instance: HTMLDivElement) => void;

  /** Function accepting the ref for the overlay */
  overlayRef?: (instance: HTMLDivElement) => void;

  /** Function that will be run after the modal has opened. */
  onAfterOpen?(): void;

  /** Function that will be run after the modal has closed. */
  onAfterClose?(): void;

  /** Function that will be run when the modal is requested to be closed, prior to actually closing. */
  onRequestClose?(event: React.MouseEvent | React.KeyboardEvent): void;

  /** Function that will be called to get the parent element that the modal will be attached to. */
  parentSelector?(): HTMLElement;
}

/**
 * Modal component
 *
 * @param {ModalProps} props
 *
 * @returns JSX.ELEMENT
 */
export const Modal = ({
  children,
  isOpen,
  className,
  overlayClassName,
  style,
  portalClassName,
  bodyOpenClassName,
  htmlOpenClassName,
  appElement,
  closeTimeoutMS,
  ariaHideApp,
  shouldFocusAfterRender,
  shouldCloseOnOverlayClick,
  shouldCloseOnEsc,
  shouldReturnFocusAfterClose,
  aria,
  data,
  role,
  contentLabel,
  contentRef,
  overlayRef,
  onAfterOpen,
  onAfterClose,
  onRequestClose,
  parentSelector
}: ModalProps) => {
  useEffect(() => {
    ReactModal.setAppElement('body');
  }, []);

  // These are the Props used from ReactModal component
  const ReacModalPropsInUse = {
    style,
    portalClassName,
    bodyOpenClassName,
    htmlOpenClassName,
    appElement,
    closeTimeoutMS: closeTimeoutMS ?? 200, // This must match the value in CSS
    ariaHideApp,
    shouldFocusAfterRender,
    shouldCloseOnOverlayClick,
    shouldCloseOnEsc,
    shouldReturnFocusAfterClose,
    aria,
    data,
    role,
    contentLabel,
    contentRef,
    overlayRef,
    onAfterOpen,
    onAfterClose,
    onRequestClose,
    parentSelector
  };
  return (
    <ReactModal
      overlayClassName={classNames([styles.overlay, overlayClassName])}
      isOpen={isOpen}
      className={classNames([styles.content, className])}
      {...ReacModalPropsInUse}
    >
      {children}
    </ReactModal>
  );
};

export default Modal;
